import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity,
  Calculator,
  CheckCircle2,
  CircleStop,
  Clock3,
  Eye,
  FileCheck2,
  Gavel,
  Loader2,
  Pause,
  Play,
  RefreshCw,
  Save,
  Send,
  ShieldAlert,
  Trophy,
  Users,
  Zap,
} from 'lucide-react';
import { Tender } from '../types';
import {
  AuctionAdapter,
  AuctionItem,
  AuctionSession,
  auctionService,
  PortalSummary,
} from '../services/auctionService';
import { useModal } from './ModalProvider';

interface AuctionRoomProps {
  tender: Tender;
  attachments: Array<{ id: string; name: string; type?: string; url: string }>;
}

const currency = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const formatMoney = (value?: number | null) => currency.format(Number(value || 0));
const formatDateTime = (value: string) => new Intl.DateTimeFormat('pt-BR', {
  dateStyle: 'short',
  timeStyle: 'medium',
}).format(new Date(value));

const statusLabel: Record<string, string> = {
  DRAFT: 'Preparação',
  RUNNING: 'Em disputa',
  PAUSED: 'Pausada',
  FINISHED: 'Encerrada',
  OPEN: 'Fase aberta',
  CLOSED_BID: 'Fase fechada',
  PREPARATION: 'Preparação',
  CLOSED: 'Encerrada',
};

const phaseLabel: Record<string, string> = {
  PREPARATION: 'Aguardando abertura',
  OPEN: 'Fase aberta',
  CLOSED_BID: 'Lance único / fase fechada',
  PAUSED: 'Pausada',
  CLOSED: 'Encerrada',
};

const syncLabel: Record<string, string> = {
  NOT_CONFIGURED: 'Sem conexão com portal',
  SIMULATED: 'Dados do simulador',
  CONNECTED: 'Atualização do portal',
  STALE: 'Atualização atrasada',
  ERROR: 'Falha na atualização',
};

const formatCountdown = (target?: string | null, now = Date.now()) => {
  if (!target) return 'Não informado pelo portal';
  const totalSeconds = Math.max(0, Math.ceil((new Date(target).getTime() - now) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const AuctionItemPanel: React.FC<{
  session: AuctionSession;
  item: AuctionItem;
  busy: boolean;
  confirmed: boolean;
  onRefresh: () => Promise<void>;
  onSession: (session: AuctionSession) => void;
}> = ({ session, item, busy, confirmed, onRefresh, onSession }) => {
  const { showAlert } = useModal();
  const [floor, setFloor] = useState(String(item.hardFloorPrice));
  const [decrement, setDecrement] = useState(String(item.decrementValue));
  const [interval, setIntervalValue] = useState(String(item.minIntervalSeconds));
  const [limit, setLimit] = useState(String(item.maxAutomaticBids));
  const [autoEnabled, setAutoEnabled] = useState(item.autoEnabled);
  const [manualAmount, setManualAmount] = useState('');
  const editable = session.status === 'DRAFT' || session.status === 'PAUSED';

  useEffect(() => {
    setFloor(String(item.hardFloorPrice));
    setDecrement(String(item.decrementValue));
    setIntervalValue(String(item.minIntervalSeconds));
    setLimit(String(item.maxAutomaticBids));
    setAutoEnabled(item.autoEnabled);
  }, [item]);

  const run = async (work: () => Promise<void>) => {
    try {
      await work();
    } catch (error) {
      await showAlert(error instanceof Error ? error.message : 'Não foi possível executar a ação.', 'Sala de pregão');
    }
  };

  const requireConfirmation = async () => {
    if (confirmed) return true;
    await showAlert('Confirme o modo de simulação no topo da sala antes de registrar lances.', 'Confirmação necessária');
    return false;
  };

  return (
    <div className="rounded-lg border border-line dark:border-line-dark bg-surface dark:bg-surface-dark">
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-3 p-4 border-b border-line dark:border-line-dark">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-brand-700 dark:text-brand-300">Item {item.itemNumber}{item.lot ? ` · Lote ${item.lot}` : ''}</span>
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">{item.status}</span>
          </div>
          <h4 className="mt-1 text-sm font-semibold text-ink-title dark:text-ink-title-dark max-h-16 overflow-hidden">{item.description}</h4>
          <p className="mt-1 text-xs text-ink-subtitle dark:text-ink-subtitle-dark">{item.quantity} {item.unit || 'UN'} · Referência {formatMoney(item.referenceValue)}</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 lg:min-w-[510px] text-right">
          <div><span className="block text-[10px] uppercase text-ink-subtitle">Melhor atual</span><strong className="text-sm text-ink-title dark:text-ink-title-dark">{formatMoney(item.currentBestBid)}</strong></div>
          <div><span className="block text-[10px] uppercase text-ink-subtitle">Nosso lance</span><strong className="text-sm text-brand-700 dark:text-brand-300">{item.ourLastBid ? formatMoney(item.ourLastBid) : '—'}</strong></div>
          <div><span className="block text-[10px] uppercase text-ink-subtitle">Nossa posição</span><strong className="text-sm text-ink-title dark:text-ink-title-dark">{item.ourPosition ? `${item.ourPosition}º` : 'Não divulgada'}</strong></div>
          <div><span className="block text-[10px] uppercase text-ink-subtitle">Concorrentes</span><strong className="text-sm text-ink-title dark:text-ink-title-dark">{item.visibleCompetitors ?? session.onlineCompetitors ?? '—'}</strong></div>
          <div><span className="block text-[10px] uppercase text-ink-subtitle">Piso</span><strong className="text-sm text-rose-700 dark:text-rose-300">{formatMoney(item.hardFloorPrice)}</strong></div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        <div className="grid sm:grid-cols-2 xl:grid-cols-5 gap-3">
          <label className="text-xs text-ink-subtitle">Piso autorizado
            <input className="input mt-1" type="number" min="0" step="0.01" disabled={!editable || busy} value={floor} onChange={(event) => setFloor(event.target.value)} />
          </label>
          <label className="text-xs text-ink-subtitle">Decremento {item.decrementType === 'PERCENT' ? '(%)' : '(R$)'}
            <input className="input mt-1" type="number" min="0.01" step="0.01" disabled={!editable || busy} value={decrement} onChange={(event) => setDecrement(event.target.value)} />
          </label>
          <label className="text-xs text-ink-subtitle">Intervalo mínimo (s)
            <input className="input mt-1" type="number" min="1" disabled={!editable || busy} value={interval} onChange={(event) => setIntervalValue(event.target.value)} />
          </label>
          <label className="text-xs text-ink-subtitle">Limite automático
            <input className="input mt-1" type="number" min="1" disabled={!editable || busy} value={limit} onChange={(event) => setLimit(event.target.value)} />
          </label>
          <div className="flex items-end gap-2">
            <label className="flex flex-1 h-10 px-3 rounded-md border border-line dark:border-line-dark items-center gap-2 text-xs text-ink-text dark:text-ink-text-dark cursor-pointer whitespace-nowrap">
              <input type="checkbox" disabled={!editable || busy} checked={autoEnabled} onChange={(event) => setAutoEnabled(event.target.checked)} />
              Autolance
            </label>
            <button
              type="button"
              className="icon-btn h-10 w-10"
              title="Salvar regras"
              disabled={!editable || busy}
              onClick={() => run(async () => {
                await auctionService.configureItem(session.id, item.id, {
                  hardFloorPrice: Number(floor),
                  decrementType: item.decrementType,
                  decrementValue: Number(decrement),
                  minIntervalSeconds: Number(interval),
                  maxAutomaticBids: Number(limit),
                  autoEnabled,
                });
                await onRefresh();
              })}
            ><Save className="w-4 h-4" /></button>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 2xl:grid-cols-[auto_minmax(260px,1fr)_auto_auto] gap-2">
          <button
            type="button"
            className="btn-secondary justify-center whitespace-nowrap"
            disabled={session.status !== 'RUNNING' || busy}
            onClick={() => run(async () => {
              const result = await auctionService.competitorBid(session.id, item.id);
              onSession(result.session);
            })}
          ><Users className="w-4 h-4" />Lance concorrente</button>

          <div className="flex min-w-0 gap-2">
            <input
              className="input flex-1"
              type="number"
              min={item.hardFloorPrice}
              step="0.01"
              placeholder="Valor do lance manual"
              disabled={session.status !== 'RUNNING' || busy}
              value={manualAmount}
              onChange={(event) => setManualAmount(event.target.value)}
            />
            <button
              type="button"
              className="btn-secondary whitespace-nowrap"
              disabled={session.status !== 'RUNNING' || busy || !manualAmount}
              onClick={() => run(async () => {
                if (!(await requireConfirmation())) return;
                const result = await auctionService.manualBid(session.id, item.id, Number(manualAmount));
                setManualAmount('');
                onSession(result.session);
              })}
            ><Send className="w-4 h-4" />Lançar</button>
          </div>

          <button
            type="button"
            className="btn-secondary justify-center whitespace-nowrap"
            disabled={session.status !== 'RUNNING' || busy}
            onClick={() => run(async () => {
              const preview = await auctionService.previewAutoBid(session.id, item.id);
              await showAlert(preview.allowed && preview.candidate
                ? `Próximo lance elegível: ${formatMoney(preview.candidate)}.`
                : preview.reason, 'Prévia de autolance');
            })}
          ><Calculator className="w-4 h-4" />Prévia</button>

          <button
            type="button"
            className="btn-primary justify-center whitespace-nowrap"
            disabled={session.status !== 'RUNNING' || !item.autoEnabled || busy}
            onClick={() => run(async () => {
              if (!(await requireConfirmation())) return;
              const result = await auctionService.executeAutoBid(session.id, item.id);
              onSession(result.session);
            })}
          ><Zap className="w-4 h-4" />Executar autolance</button>
        </div>
        <div className="text-[11px] text-ink-subtitle dark:text-ink-subtitle-dark">
          Automáticos: {item.automaticBidCount}/{item.maxAutomaticBids}
          {item.lastAutomaticBidAt ? ` · Último em ${formatDateTime(item.lastAutomaticBidAt)}` : ''}
        </div>
      </div>
    </div>
  );
};

const AuctionRoom: React.FC<AuctionRoomProps> = ({ tender, attachments }) => {
  const { showAlert } = useModal();
  const [adapters, setAdapters] = useState<AuctionAdapter[]>([]);
  const [portals, setPortals] = useState<PortalSummary[]>([]);
  const [sessions, setSessions] = useState<AuctionSession[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState('');
  const [adapterKey, setAdapterKey] = useState('COMPRAS_GOV_SIMULATOR');
  const [portalCredentialId, setPortalCredentialId] = useState('');
  const [referenceValue, setReferenceValue] = useState(String(Math.max(Number(tender.value || 0), 1)));
  const [floorValue, setFloorValue] = useState(String(Math.max(Number(tender.value || 0) * 0.7, 0.01).toFixed(2)));
  const [decrementValue, setDecrementValue] = useState('1');
  const [openPhaseMinutes, setOpenPhaseMinutes] = useState('15');
  const [closedPhaseMinutes, setClosedPhaseMinutes] = useState('5');
  const [confirmed, setConfirmed] = useState(false);
  const [autoCycle, setAutoCycle] = useState(false);
  const [busy, setBusy] = useState(false);
  const [clockNow, setClockNow] = useState(Date.now());

  const selectedSession = useMemo(
    () => sessions.find((session) => session.id === selectedSessionId) || sessions[0] || null,
    [sessions, selectedSessionId],
  );
  const selectedAdapter = adapters.find((adapter) => adapter.key === adapterKey) || adapters[0];

  const loadSessions = useCallback(async () => {
    const data = await auctionService.getSessions(tender.id);
    setSessions(data);
    setSelectedSessionId((current) => data.some((session) => session.id === current) ? current : data[0]?.id || '');
  }, [tender.id]);

  useEffect(() => {
    setBusy(true);
    Promise.all([auctionService.getAdapters(), auctionService.getPortals(), auctionService.getSessions(tender.id)])
      .then(([adapterData, portalData, sessionData]) => {
        setAdapters(adapterData);
        setPortals(portalData);
        setSessions(sessionData);
        setSelectedSessionId(sessionData[0]?.id || '');
        if (adapterData[0]) setAdapterKey(adapterData[0].key);
      })
      .catch((error) => showAlert(error instanceof Error ? error.message : 'Erro ao carregar a sala.', 'Sala de pregão'))
      .finally(() => setBusy(false));
  }, [showAlert, tender.id]);

  useEffect(() => {
    setReferenceValue(String(Math.max(Number(tender.value || 0), 1)));
    setFloorValue(String(Math.max(Number(tender.value || 0) * 0.7, 0.01).toFixed(2)));
  }, [tender.id, tender.value]);

  useEffect(() => {
    if (selectedAdapter?.formats[0]?.key === 'LOT_TOTAL') setDecrementValue('0.5');
    else setDecrementValue('1');
  }, [selectedAdapter?.key]);

  useEffect(() => {
    const interval = window.setInterval(() => setClockNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!autoCycle || !selectedSession || selectedSession.status !== 'RUNNING' || !confirmed) return;
    let running = false;
    const timer = window.setInterval(async () => {
      if (running) return;
      running = true;
      try {
        const result = await auctionService.runAutoCycle(selectedSession.id);
        setSessions((current) => current.map((entry) => entry.id === result.session.id ? result.session : entry));
      } catch (error) {
        console.info('[auction] Ciclo sem lance:', error);
      } finally {
        running = false;
      }
    }, 5000);
    return () => window.clearInterval(timer);
  }, [autoCycle, confirmed, selectedSession?.id, selectedSession?.status]);

  const replaceSession = (session: AuctionSession) => {
    setSessions((current) => current.map((entry) => entry.id === session.id ? session : entry));
  };

  const perform = async (work: () => Promise<void>) => {
    try {
      setBusy(true);
      await work();
    } catch (error) {
      await showAlert(error instanceof Error ? error.message : 'Não foi possível executar a ação.', 'Sala de pregão');
    } finally {
      setBusy(false);
    }
  };

  const createScenario = () => perform(async () => {
    if (!selectedAdapter) throw new Error('Selecione um perfil de portal.');
    const format = selectedAdapter.formats[0].key;
    const created = await auctionService.createSession({
      tenderId: tender.id,
      adapterKey: selectedAdapter.key,
      format,
      portalCredentialId: portalCredentialId || null,
      title: `${tender.title} - ${selectedAdapter.name}`,
      openPhaseSeconds: Math.max(1, Number(openPhaseMinutes) || 15) * 60,
      closedPhaseSeconds: Math.max(1, Number(closedPhaseMinutes) || 5) * 60,
      portalName: tender.portal || undefined,
      items: [{
        itemNumber: '1',
        lot: format === 'LOT_TOTAL' ? '1' : null,
        description: tender.description || tender.title,
        quantity: 1,
        unit: 'UN',
        referenceValue: Number(referenceValue),
        hardFloorPrice: Number(floorValue),
        decrementType: format === 'LOT_TOTAL' ? 'PERCENT' : 'FIXED',
        decrementValue: Number(decrementValue),
        minIntervalSeconds: 20,
        maxAutomaticBids: 20,
        autoEnabled: false,
      }],
    });
    setSessions((current) => [created, ...current]);
    setSelectedSessionId(created.id);
  });

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 px-4 py-3 flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 text-amber-700 dark:text-amber-300 mt-0.5 flex-shrink-0" />
          <div>
            <div className="text-sm font-semibold text-amber-900 dark:text-amber-100">Ambiente de simulação</div>
            <div className="text-xs text-amber-800 dark:text-amber-200">Nenhum lance, credencial ou documento é enviado aos portais externos.</div>
          </div>
        </div>
        <label className="flex items-center gap-2 text-xs font-medium text-amber-900 dark:text-amber-100">
          <input type="checkbox" checked={confirmed} onChange={(event) => {
            setConfirmed(event.target.checked);
            if (!event.target.checked) setAutoCycle(false);
          }} />
          Confirmo os limites desta simulação
        </label>
      </div>

      <div className="grid xl:grid-cols-[360px_minmax(0,1fr)] gap-4">
        <aside className="space-y-4">
          <section className="rounded-lg border border-line dark:border-line-dark bg-surface dark:bg-surface-dark p-4 space-y-3">
            <div className="flex items-center gap-2"><Gavel className="w-4 h-4 text-brand-600" /><h3 className="text-sm font-semibold text-ink-title dark:text-ink-title-dark">Novo cenário</h3></div>
            <label className="block text-xs text-ink-subtitle">Perfil de disputa
              <select className="input mt-1" value={adapterKey} onChange={(event) => setAdapterKey(event.target.value)}>
                {adapters.map((adapter) => <option key={adapter.key} value={adapter.key}>{adapter.name} · {adapter.formats[0]?.name}</option>)}
              </select>
            </label>
            {selectedAdapter && <p className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark">{selectedAdapter.behavior}</p>}
            <label className="block text-xs text-ink-subtitle">Credencial de referência
              <select className="input mt-1" value={portalCredentialId} onChange={(event) => setPortalCredentialId(event.target.value)}>
                <option value="">Sem credencial vinculada</option>
                {portals.map((portal) => <option key={portal.id} value={portal.id}>{portal.name}</option>)}
              </select>
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="text-xs text-ink-subtitle">Referência
                <input className="input mt-1" type="number" min="0.01" step="0.01" value={referenceValue} onChange={(event) => setReferenceValue(event.target.value)} />
              </label>
              <label className="text-xs text-ink-subtitle">Piso
                <input className="input mt-1" type="number" min="0" step="0.01" value={floorValue} onChange={(event) => setFloorValue(event.target.value)} />
              </label>
            </div>
            <label className="block text-xs text-ink-subtitle">Decremento {selectedAdapter?.formats[0]?.key === 'LOT_TOTAL' ? '(%)' : '(R$)'}
              <input className="input mt-1" type="number" min="0.01" step="0.01" value={decrementValue} onChange={(event) => setDecrementValue(event.target.value)} />
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="text-xs text-ink-subtitle">Fase aberta (min)
                <input className="input mt-1" type="number" min="1" value={openPhaseMinutes} onChange={(event) => setOpenPhaseMinutes(event.target.value)} />
              </label>
              <label className="text-xs text-ink-subtitle">Lance único (min)
                <input className="input mt-1" type="number" min="1" disabled={selectedAdapter?.formats[0]?.key !== 'OPEN_CLOSED'} value={closedPhaseMinutes} onChange={(event) => setClosedPhaseMinutes(event.target.value)} />
              </label>
            </div>
            <button type="button" className="btn-primary w-full" disabled={busy} onClick={createScenario}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gavel className="w-4 h-4" />}Criar simulação
            </button>
          </section>

          <section className="rounded-lg border border-line dark:border-line-dark bg-surface dark:bg-surface-dark p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-ink-title dark:text-ink-title-dark">Sessões</h3>
              <button type="button" className="icon-btn" title="Atualizar sessões" onClick={() => perform(loadSessions)}><RefreshCw className="w-4 h-4" /></button>
            </div>
            {sessions.length === 0 ? <p className="text-xs text-ink-subtitle">Nenhuma simulação criada para esta licitação.</p> : (
              <div className="space-y-2 max-h-72 overflow-auto">
                {sessions.map((session) => (
                  <button
                    key={session.id}
                    type="button"
                    onClick={() => setSelectedSessionId(session.id)}
                    className={`w-full text-left rounded-md border px-3 py-2 ${selectedSession?.id === session.id ? 'border-brand-500 bg-brand-50 dark:bg-[rgba(203,106,30,0.15)]' : 'border-line dark:border-line-dark'}`}
                  >
                    <span className="block text-xs font-semibold text-ink-title dark:text-ink-title-dark truncate">{session.portalName}</span>
                    <span className="block text-[11px] text-ink-subtitle mt-0.5">{statusLabel[session.status] || session.status} · {session.items.length} item(ns)</span>
                  </button>
                ))}
              </div>
            )}
          </section>
        </aside>

        <main className="min-w-0 space-y-4">
          {!selectedSession ? (
            <div className="rounded-lg border border-line dark:border-line-dark p-10 text-center text-sm text-ink-subtitle">Crie um cenário para abrir a sala de pregão.</div>
          ) : (
            <>
              <section className="rounded-lg border border-line dark:border-line-dark bg-surface dark:bg-surface-dark p-4">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-ink-title dark:text-ink-title-dark">{selectedSession.portalName}</h3>
                      <span className="text-[11px] rounded-full px-2 py-0.5 bg-brand-50 text-brand-700 dark:bg-brand-950/30 dark:text-brand-300">{statusLabel[selectedSession.status] || selectedSession.status}</span>
                    </div>
                    <p className="text-xs text-ink-subtitle mt-1">{selectedSession.title} · {statusLabel[selectedSession.phase] || selectedSession.phase}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 [&_button]:whitespace-nowrap">
                    {selectedSession.status !== 'RUNNING' && selectedSession.status !== 'FINISHED' && (
                      <button type="button" className="btn-primary" disabled={busy} onClick={() => perform(async () => replaceSession(await auctionService.start(selectedSession.id)))}><Play className="w-4 h-4" />Iniciar</button>
                    )}
                    {selectedSession.status === 'RUNNING' && (
                      <button type="button" className="btn-secondary" disabled={busy} onClick={() => perform(async () => {
                        setAutoCycle(false);
                        replaceSession(await auctionService.pause(selectedSession.id));
                      })}><Pause className="w-4 h-4" />Pausar</button>
                    )}
                    {selectedSession.format === 'OPEN_CLOSED' && selectedSession.status === 'RUNNING' && (
                      <button type="button" className="btn-secondary" disabled={busy} onClick={() => perform(async () => replaceSession(await auctionService.setPhase(selectedSession.id, selectedSession.phase === 'OPEN' ? 'CLOSED_BID' : 'OPEN')))}>
                        <RefreshCw className="w-4 h-4" />{selectedSession.phase === 'OPEN' ? 'Fase fechada' : 'Fase aberta'}
                      </button>
                    )}
                    {selectedSession.status === 'RUNNING' && (
                      <label className="btn-secondary cursor-pointer">
                        <input
                          type="checkbox"
                          checked={autoCycle}
                          disabled={!confirmed}
                          onChange={(event) => setAutoCycle(event.target.checked)}
                        />
                        <Activity className="w-4 h-4" />Ciclo contínuo
                      </label>
                    )}
                    {selectedSession.status !== 'FINISHED' && (
                      <button type="button" className="icon-btn" title="Encerrar simulação" disabled={busy} onClick={() => perform(async () => {
                        setAutoCycle(false);
                        replaceSession(await auctionService.finish(selectedSession.id));
                      })}><CircleStop className="w-4 h-4" /></button>
                    )}
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-2 xl:grid-cols-5 gap-3">
                  <div className="rounded-md border border-line dark:border-line-dark px-3 py-2">
                    <span className="text-[10px] uppercase text-ink-subtitle">Modalidade</span>
                    <strong className="mt-0.5 block text-xs text-ink-title dark:text-ink-title-dark">{selectedSession.disputeMode === 'OPEN_CLOSED' ? 'Aberto e fechado' : selectedSession.disputeMode === 'CLOSED' ? 'Fechado' : 'Aberto'}</strong>
                  </div>
                  <div className="rounded-md border border-line dark:border-line-dark px-3 py-2">
                    <span className="text-[10px] uppercase text-ink-subtitle">Fase atual</span>
                    <strong className="mt-0.5 block text-xs text-ink-title dark:text-ink-title-dark">{phaseLabel[selectedSession.phase] || selectedSession.phase}</strong>
                  </div>
                  <div className={`rounded-md border px-3 py-2 ${selectedSession.phase === 'CLOSED_BID' ? 'border-amber-400 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30' : 'border-line dark:border-line-dark'}`}>
                    <span className="flex items-center gap-1 text-[10px] uppercase text-ink-subtitle"><Clock3 className="w-3 h-3" />Cronômetro</span>
                    <strong className="mt-0.5 block text-sm tabular-nums text-ink-title dark:text-ink-title-dark">{formatCountdown(selectedSession.phaseEndsAt, clockNow)}</strong>
                  </div>
                  <div className={`rounded-md border px-3 py-2 ${selectedSession.phase === 'CLOSED_BID' ? 'border-amber-500 bg-amber-100 dark:border-amber-700 dark:bg-amber-950/50' : 'border-line dark:border-line-dark'}`}>
                    <span className="text-[10px] uppercase text-ink-subtitle">Lance único</span>
                    <strong className="mt-0.5 block text-xs text-ink-title dark:text-ink-title-dark">{selectedSession.phase === 'CLOSED_BID' ? 'Disponível agora' : selectedSession.singleBidAt ? formatDateTime(selectedSession.singleBidAt) : 'Não aplicável'}</strong>
                  </div>
                  <div className="rounded-md border border-line dark:border-line-dark px-3 py-2">
                    <span className="flex items-center gap-1 text-[10px] uppercase text-ink-subtitle"><Users className="w-3 h-3" />Online</span>
                    <strong className="mt-0.5 block text-xs text-ink-title dark:text-ink-title-dark">{selectedSession.onlineCompetitors ?? 'Não divulgado'} concorrente(s)</strong>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-ink-subtitle dark:text-ink-subtitle-dark">
                  <span className="inline-flex items-center gap-1"><Eye className="w-3.5 h-3.5" />{syncLabel[selectedSession.syncStatus] || selectedSession.syncStatus}</span>
                  <span>Última atualização: {selectedSession.lastPortalSyncAt ? formatDateTime(selectedSession.lastPortalSyncAt) : 'ainda não recebida'}</span>
                  {selectedSession.syncMessage && <span>{selectedSession.syncMessage}</span>}
                </div>
              </section>

              {selectedSession.items.map((item) => (
                <AuctionItemPanel
                  key={item.id}
                  session={selectedSession}
                  item={item}
                  busy={busy}
                  confirmed={confirmed}
                  onRefresh={loadSessions}
                  onSession={replaceSession}
                />
              ))}

              <section className="rounded-lg border border-line dark:border-line-dark bg-surface dark:bg-surface-dark p-4">
                <div className="flex items-center gap-2"><Trophy className="w-4 h-4 text-brand-600" /><h3 className="text-sm font-semibold text-ink-title dark:text-ink-title-dark">Propostas iniciais e classificação</h3></div>
                {!selectedSession.initialProposalsVisible ? (
                  <p className="mt-3 text-xs text-ink-subtitle dark:text-ink-subtitle-dark">Aguardando a liberação dessas informações pelo pregoeiro ou pelo portal.</p>
                ) : (
                  <div className="mt-3 space-y-3">
                    {selectedSession.items.map((item) => (
                      <div key={item.id} className="rounded-md border border-line dark:border-line-dark overflow-hidden">
                        <div className="px-3 py-2 text-xs font-semibold text-ink-title dark:text-ink-title-dark">Item {item.itemNumber}{item.lot ? ` · Lote ${item.lot}` : ''}</div>
                        {item.initialProposals.length === 0 ? <p className="px-3 pb-3 text-xs text-ink-subtitle">Nenhuma proposta inicial recebida nesta sincronização.</p> : (
                          <div className="divide-y divide-line dark:divide-line-dark">
                            {item.initialProposals.map((proposal) => <div key={proposal.id} className={`grid grid-cols-[56px_minmax(0,1fr)_auto] gap-3 px-3 py-2 text-xs ${proposal.isOurProposal ? 'bg-emerald-50 dark:bg-emerald-950/20' : ''}`}>
                              <span className="font-semibold text-ink-subtitle">{proposal.position ? `${proposal.position}º` : '—'}</span>
                              <span className="truncate text-ink-text dark:text-ink-text-dark">{proposal.companyName}{proposal.isOurProposal ? ' (nossa empresa)' : ''}</span>
                              <span className="font-semibold text-ink-title dark:text-ink-title-dark">{formatMoney(proposal.amount)}</span>
                            </div>)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <div className="grid lg:grid-cols-2 gap-4">
                <section className="rounded-lg border border-line dark:border-line-dark bg-surface dark:bg-surface-dark p-4 space-y-3">
                  <div className="flex items-center gap-2"><FileCheck2 className="w-4 h-4 text-brand-600" /><h3 className="text-sm font-semibold text-ink-title dark:text-ink-title-dark">Documentos da sessão</h3></div>
                  <div className="space-y-2 max-h-72 overflow-auto">
                    {attachments.length === 0 ? <p className="text-xs text-ink-subtitle">Nenhum anexo disponível nesta licitação.</p> : attachments.map((attachment) => {
                      const linked = selectedSession.documents.find((document) => document.attachment?.id === attachment.id);
                      return (
                        <div key={attachment.id} className="flex items-center justify-between gap-2 rounded-md border border-line dark:border-line-dark px-3 py-2">
                          <div className="min-w-0"><span className="block text-xs font-medium truncate">{attachment.name}</span><span className="text-[10px] text-ink-subtitle">{linked ? linked.status : 'Não separado'}</span></div>
                          {!linked ? (
                            <button type="button" className="btn-secondary" disabled={busy} onClick={() => perform(async () => {
                              await auctionService.addDocument(selectedSession.id, attachment.id, false);
                              await loadSessions();
                            })}><FileCheck2 className="w-4 h-4" />Separar</button>
                          ) : linked.status !== 'SIMULATED' ? (
                            <button type="button" className="btn-secondary" disabled={busy} onClick={() => perform(async () => {
                              await auctionService.simulateDocumentUpload(selectedSession.id, linked.id);
                              await loadSessions();
                            })}><CheckCircle2 className="w-4 h-4" />Simular upload</button>
                          ) : <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
                        </div>
                      );
                    })}
                  </div>
                </section>

                <section className="rounded-lg border border-line dark:border-line-dark bg-surface dark:bg-surface-dark p-4 space-y-3">
                  <div className="flex items-center gap-2"><Activity className="w-4 h-4 text-brand-600" /><h3 className="text-sm font-semibold text-ink-title dark:text-ink-title-dark">Diário da disputa</h3></div>
                  <div className="space-y-2 max-h-72 overflow-auto">
                    {selectedSession.events.map((event) => (
                      <div key={event.id} className="border-b border-line dark:border-line-dark pb-2 last:border-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className={`text-[10px] font-semibold ${event.status === 'ACCEPTED' ? 'text-emerald-700 dark:text-emerald-300' : event.status === 'REJECTED' ? 'text-rose-700 dark:text-rose-300' : 'text-amber-700 dark:text-amber-300'}`}>{event.source} · {event.status}</span>
                          <span className="text-[10px] text-ink-subtitle">{formatDateTime(event.occurredAt)}</span>
                        </div>
                        <p className="text-xs text-ink-text dark:text-ink-text-dark mt-1">{event.item ? `Item ${event.item.itemNumber}: ` : ''}{event.reason}{event.amount ? ` · ${formatMoney(event.amount)}` : ''}</p>
                      </div>
                    ))}
                  </div>
                </section>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default AuctionRoom;
